import React, { FC, useState, useCallback } from 'react'
import { Modal, Button, message } from 'antd'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import MilkdownEditor from 'components/MilkdownEditor'
import { UPDATE_DOCUMENT_MARKDOWN_MUTATION } from 'graphql/LegalFormalities'
import { LegalDocumentType } from 'types'
import { MarkdownVersion } from 'types/legalDocument'

interface MarkdownEditorModalProps {
  open: boolean
  onClose: () => void
  document: LegalDocumentType
  onSaved?: (document: LegalDocumentType) => void
}

const getLatestMarkdown = (
  markdownContent: MarkdownVersion[] | null | undefined
): string => {
  if (!markdownContent || markdownContent.length === 0) return ''
  return markdownContent[markdownContent.length - 1].content || ''
}

const MarkdownEditorModal: FC<MarkdownEditorModalProps> = ({
  open,
  onClose,
  document,
  onSaved,
}) => {
  const { t } = useTranslation()
  const defaultMarkdown = getLatestMarkdown(document?.markdownContent)
  const [currentMarkdown, setCurrentMarkdown] = useState(defaultMarkdown)
  const [saving, setSaving] = useState(false)

  const [updateDocumentMarkdown] = useMutation(
    UPDATE_DOCUMENT_MARKDOWN_MUTATION
  )

  const handleChange = useCallback((markdown: string) => {
    setCurrentMarkdown(markdown)
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await updateDocumentMarkdown({
        variables: {
          legalDocumentId: document.id,
          content: currentMarkdown,
        },
      })
      const result = response.data?.legalFormalityUpdateDocumentMarkdown
      if (result?.error) {
        message.error(result.error)
      } else {
        message.success(
          t('legalFormalities:show.misc.markdownSaved', {
            defaultValue: 'Document sauvegardé et PDF régénéré',
          })
        )
        onSaved?.(result?.legalFormalityDocument)
        onClose()
      }
    } catch (e: any) {
      message.error(e.message || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={t('legalFormalities:show.misc.editMarkdown', {
        defaultValue: 'Éditer le document',
      })}
      open={open}
      onCancel={onClose}
      width="90vw"
      style={{ top: 20 }}
      bodyStyle={{ height: '75vh', overflow: 'auto' }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('legalFormalities:show.misc.cancel', {
            defaultValue: 'Annuler',
          })}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          {t('legalFormalities:show.misc.saveAndRegenerate', {
            defaultValue: 'Sauvegarder & Régénérer PDF',
          })}
        </Button>,
      ]}
    >
      {open && defaultMarkdown && (
        <MilkdownEditor
          defaultValue={defaultMarkdown}
          onChange={handleChange}
        />
      )}
      {open && !defaultMarkdown && (
        <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
          {t('legalFormalities:show.misc.noMarkdownContent', {
            defaultValue: 'Aucun contenu markdown disponible pour ce document.',
          })}
        </div>
      )}
    </Modal>
  )
}

export default MarkdownEditorModal
